interface ExtendedNotificationOptions extends NotificationOptions {
  vibrate?: number[];
  tag?: string;
  renotify?: boolean;
  badge?: string;
}

import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  orderBy, 
  Timestamp,
  UpdateData,
  DocumentData
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { FuelConsumption, FuelConsumptionInput } from '../types/fuelConsumption';
import { notificationService } from './notificationService';
import { organizationService } from './organizationService';
import { vehicleService } from './vehicleService';
import { userService } from './userService';

class FuelConsumptionService {
  private readonly COLLECTION = 'fuelConsumptions';

  async create(data: FuelConsumptionInput, userId: string, receiptFile?: File): Promise<string> {
    try {
      let receiptUrl: string | undefined;

      if (receiptFile) {
        const storageRef = ref(storage, `fuel/${data.organizationId}/${data.vehicleId}/${Date.now()}_${receiptFile.name}`);
        await uploadBytes(storageRef, receiptFile);
        receiptUrl = await getDownloadURL(storageRef);
      }

      // Créer un nouvel objet sans receiptUrl
      const { receiptUrl: _, ...cleanData } = data;
      
      const docData = {
        ...cleanData,
        createdAt: Timestamp.fromDate(new Date()),
        date: Timestamp.fromDate(data.date),
        createdBy: userId
      };

      // Ajouter receiptUrl seulement s'il existe
      if (receiptUrl) {
        Object.assign(docData, { receiptUrl });
      }

      const docRef = await addDoc(collection(db, this.COLLECTION), docData);

      try {
        // Récupérer les informations du véhicule
        const vehicle = await vehicleService.getVehicle(data.vehicleId);
        
        if (vehicle) {
          console.log('Vehicle found:', vehicle);
          console.log('Organization ID:', data.organizationId);
          
          // Récupérer les admins de l'organisation
          const admins = await organizationService.getOrganizationAdmins(data.organizationId);
          console.log('Found organization admins:', admins);
          
          if (admins.length === 0) {
            console.warn('No admins found for organization:', data.organizationId);
          }

          // Envoyer une notification à chaque admin
          for (const admin of admins) {
            console.log('Sending notification to admin:', admin);
            // Notification email
            await notificationService.sendNotificationEmail({
              to_email: admin.email,
              to_name: admin.displayName || admin.email,
              subject: `Nouveau ticket carburant - ${vehicle.make} ${vehicle.model}`,
              message: notificationService.formatFuelMessage({
                id: docRef.id,
                vehicleId: docData.vehicleId,
                organizationId: docData.organizationId,
                date: docData.date.toDate(),
                amount: docData.amount,
                volume: docData.volume,
                supplier: docData.supplier,
                createdBy: docData.createdBy,
                createdAt: docData.createdAt.toDate()
              }, vehicle)
            });

            // Récupérer l'utilisateur par email pour les préférences
            const user = await userService.findUserByEmail(admin.email);
            if (!user?.id) continue;

            // Notification push si activée
            const userPrefs = await notificationService.getNotificationPreferences(user.id);
            if (userPrefs.enabled && userPrefs.fuel) {
              // S'assurer que le service worker est enregistré
              const registration = await navigator.serviceWorker.ready;
              if (registration) {
                const notificationOptions: ExtendedNotificationOptions = {
                  body: `Montant: ${data.amount}€\nVolume: ${data.volume}L`,
                  icon: '/logo-verifleet1.png',
                  badge: '/logo-verifleet1.png',
                  data: {
                    url: `/admin/vehicles/${vehicle.id}/fuel`
                  },
                  vibrate: [200, 100, 200],
                  requireInteraction: true,
                  tag: 'verifleet-notification',
                  renotify: true
                };
                
                await registration.showNotification(
                  `Nouveau ticket carburant - ${vehicle.make} ${vehicle.model}`,
                  notificationOptions
                );
              }
            }
          }
        }
      } catch (notifError) {
        // Log l'erreur mais ne pas bloquer la création du ticket carburant
        console.error('Error sending fuel consumption notification:', notifError);
      }

      return docRef.id;
    } catch (error) {
      console.error('Error creating fuel consumption:', error);
      throw new Error('Failed to create fuel consumption record');
    }
  }

  async getByVehicle(vehicleId: string): Promise<FuelConsumption[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where('vehicleId', '==', vehicleId),
        orderBy('date', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: (doc.data().date as Timestamp).toDate(),
        createdAt: (doc.data().createdAt as Timestamp).toDate()
      })) as FuelConsumption[];
    } catch (error) {
      console.error('Error fetching fuel consumptions:', error);
      throw new Error('Failed to fetch fuel consumption records');
    }
  }

  async getBySupplier(supplierId: string): Promise<FuelConsumption[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where('supplier', '==', supplierId),
        orderBy('date', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: (doc.data().date as Timestamp).toDate(),
        createdAt: (doc.data().createdAt as Timestamp).toDate()
      })) as FuelConsumption[];
    } catch (error) {
      console.error('Error fetching fuel consumptions:', error);
      throw new Error('Failed to fetch fuel consumption records');
    }
  }

  async getByOrganization(organizationId: string): Promise<FuelConsumption[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where('organizationId', '==', organizationId),
        orderBy('date', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: (doc.data().date as Timestamp).toDate(),
        createdAt: (doc.data().createdAt as Timestamp).toDate()
      })) as FuelConsumption[];
    } catch (error) {
      console.error('Error fetching fuel consumptions:', error);
      throw new Error('Failed to fetch fuel consumption records');
    }
  }

  async update(id: string, data: Partial<FuelConsumptionInput>): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION, id);
      const updateData = Object.entries(data).reduce((acc, [key, value]) => {
        if (key === 'date' && value instanceof Date) {
          acc[key] = Timestamp.fromDate(value);
        } else {
          acc[key] = value;
        }
        return acc;
      }, {} as UpdateData<DocumentData>);

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating fuel consumption:', error);
      throw new Error('Failed to update fuel consumption record');
    }
  }

  async delete(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, this.COLLECTION, id));
    } catch (error) {
      console.error('Error deleting fuel consumption:', error);
      throw new Error('Failed to delete fuel consumption record');
    }
  }
}

export const fuelConsumptionService = new FuelConsumptionService();
